"use client"
import { refreshSisterSiteSessions } from "@/app/actions/auth"
import { SisterSiteKey } from "@/types/auth"
import { useSession } from "next-auth/react"
import { useCallback, useRef } from "react"
import React from "react"

const TOKEN_TTL = 10 * 60 * 1000

type CacheEntry = { token: string | null; fetchedAt: number }

// Module-level: survives component unmount/remount within the same page session
const moduleCache: Partial<Record<SisterSiteKey, CacheEntry>> = {}
let sharedInflight: Promise<void> | null = null

export function useSisterSiteNavigation() {
   const { data: session } = useSession()
   const cache = useRef(moduleCache)

   // Seed from JWT session tokens when cache is empty — skips the network call for
   // logged-in users whose tokens are already in their session.
   // Use sessions_fetched_at from the JWT so the TTL reflects when tokens were actually
   // fetched, not when this page was loaded.
   if (session && !moduleCache.training && !moduleCache.jobs && !moduleCache.events) {
      const fetchedAt = session.sessions_fetched_at ?? Date.now()
      if (session.training_session) moduleCache.training = { token: session.training_session, fetchedAt }
      if (session.jobs_session) moduleCache.jobs = { token: session.jobs_session, fetchedAt }
      if (session.webinar_session) moduleCache.events = { token: session.webinar_session, fetchedAt }
   }

   const fetchToken = useCallback(async (sisterSiteKey: SisterSiteKey): Promise<string | null> => {
      const cached = cache.current[sisterSiteKey]
      if (cached && Date.now() - cached.fetchedAt < TOKEN_TTL) return cached.token

      if (!sharedInflight) {
         sharedInflight = refreshSisterSiteSessions().then((fresh) => {
            const now = Date.now()
            if (fresh) {
               cache.current.training = { token: fresh.training_session, fetchedAt: now }
               cache.current.jobs = { token: fresh.jobs_session, fetchedAt: now }
               cache.current.events = { token: fresh.webinar_session, fetchedAt: now }
            }
            sharedInflight = null
         })
      }

      await sharedInflight
      return cache.current[sisterSiteKey]?.token ?? null
   }, [])

   function buildHandler(baseHref: string, sisterSiteKey: SisterSiteKey) {
      return async (e: React.MouseEvent<HTMLAnchorElement>) => {
         e.preventDefault()
         const isNewTab = e.button === 1 || e.ctrlKey || e.metaKey
         const token = await fetchToken(sisterSiteKey)
         const url = token ? `${baseHref}?session=${token}` : baseHref
         if (isNewTab) {
            window.open(url, "_blank", "noopener,noreferrer")
         } else {
            window.location.href = url
         }
      }
   }

   function buildPrefetch(sisterSiteKey: SisterSiteKey) {
      return () => void fetchToken(sisterSiteKey)
   }

   return { buildHandler, buildPrefetch, fetchToken }
}
